import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Collapse, Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import AddEditNetwork from "./AddEditNetwork";
import ActionMenu from "../../components/SharedComp/ActionMenu";

/**
//    * @component  Network row (Zone–Network mapping)
//    * @description  E5 design: the row header toggles; expanding opens the Edit Network form directly.
//    * ⋯ holds Edit Network / Deactivate Mapping.
//    * @returns network row UI
//    */
const DropdownItem = (props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // row expanded = edit form shown

  /**
   * toggle the row; it can't be toggled while its form is saving (design)
   */
  const onDropdownClicked = () => {
    if (props.loaderSave || props.dialogLoader) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  // ⋯ → Edit Network: same as expanding the row
  const onClickEdit = () => {
    setIsDropdownOpen(true);
  };

  // form closed (Cancel, "No" in save-before-closing, or saved): collapse the row
  const onClickDialog = () => {
    props.handleModalClose();
    setIsDropdownOpen(false);
  };

  return (
    <Box sx={{ mb: "12px" }}>
      <div id={props.id}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={isDropdownOpen}
          onClick={onDropdownClicked}
          onKeyDown={(e) => {
            if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onDropdownClicked();
            }
          }}
          sx={{
            backgroundColor: "#eeeef4",
            borderRadius: isDropdownOpen ? "8px 8px 0 0" : "8px",
            display: "flex",
            alignItems: "center",
            p: "6px 16px 6px 14px",
            fontSize: 13,
            minHeight: 40,
            boxSizing: "border-box",
            cursor: "pointer",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, mr: "14px", color: "#202020" }}>
            {props.data.networkNumber}
          </Box>
          <Box component="span" sx={{ color: "#864797", fontWeight: 700, flex: 1 }}>
            {props.title}
          </Box>
          <ActionMenu
            ariaLabel={`Network actions for ${props.title}`}
            sx={{ width: 26, height: 26, mr: "10px" }}
            items={[
              { label: "Edit Network", onClick: onClickEdit },
              {
                label: "Deactivate Mapping",
                destructive: true,
                onClick: () =>
                  props.onDeactivateMapping && props.onDeactivateMapping(props.data),
              },
            ]}
          />
          <Button
            aria-label={isDropdownOpen ? "Collapse" : "Expand"}
            onClick={(e) => {
              e.stopPropagation();
              onDropdownClicked();
            }}
            sx={{ minWidth: 32, color: "#333" }}
          >
            <FontAwesomeIcon icon={isDropdownOpen ? faAngleUp : faAngleDown} />
          </Button>
        </Box>
        <Collapse in={isDropdownOpen} unmountOnExit>
          <Box
            sx={{
              border: "1px solid #e3e3e8",
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              p: "16px 20px 18px",
              backgroundColor: "#fff",
            }}
          >
            <AddEditNetwork
              {...props}
              onClose={props.onClose}
              handleModalClose={onClickDialog}
              hightLightFields={props.hightLightFields}
            />
          </Box>
        </Collapse>
      </div>
    </Box>
  );
};

DropdownItem.propTypes = {
  data: PropTypes.object,
  title: PropTypes.string,
  onClose: PropTypes.func,
};

export default DropdownItem;
